import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

const editApplicantSchema = z
  .object({
    applicantName: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
    applicantEmail: z.string().email("Email inválido").optional().or(z.literal("")),
    applicantPhone: z.string().optional(),
    linkedinUrl: z.string().optional(),
  })
  .refine(
    (data) =>
      (data.applicantEmail && data.applicantEmail.length > 0) ||
      (data.applicantPhone && data.applicantPhone.length > 0) ||
      (data.linkedinUrl && data.linkedinUrl.length > 0),
    {
      message: "Informe pelo menos um contato: Email, Telefone ou LinkedIn",
      path: ["applicantEmail"],
    },
  );

type EditApplicantValues = z.infer<typeof editApplicantSchema>;

interface EditApplicantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  initialData: {
    applicantName: string;
    applicantEmail: string;
    applicantPhone?: string;
    linkedinUrl?: string;
  };
  onSuccess?: () => void;
}

export function EditApplicantDialog({
  open,
  onOpenChange,
  applicationId,
  initialData,
  onSuccess,
}: EditApplicantDialogProps) {
  const { toast } = useToast();

  const form = useForm<EditApplicantValues>({
    resolver: zodResolver(editApplicantSchema),
    defaultValues: {
      applicantName: initialData.applicantName || "",
      applicantEmail: initialData.applicantEmail || "",
      applicantPhone: initialData.applicantPhone || "",
      linkedinUrl: initialData.linkedinUrl || "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        applicantName: initialData.applicantName || "",
        applicantEmail: initialData.applicantEmail || "",
        applicantPhone: initialData.applicantPhone || "",
        linkedinUrl: initialData.linkedinUrl || "",
      });
    }
  }, [open, initialData, form.reset]);

  const updateApplicant = useMutation({
    mutationFn: async (data: EditApplicantValues) => {
      return rpcClient.applications.editApplicantInfo({ id: applicationId, ...data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ id: applicationId }).queryKey,
      });
      toast({
        title: "Candidato atualizado",
        description: "As informações foram salvas com sucesso.",
      });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Falha ao atualizar candidato",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: EditApplicantValues) => {
    updateApplicant.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Candidato</DialogTitle>
          <DialogDescription>Atualize as informações do candidato.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="applicantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome Completo</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Jane Smith"
                        {...field}
                        data-testid="input-edit-applicant-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicantEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (opcional)</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="jane@email.com"
                        {...field}
                        data-testid="input-edit-applicant-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="applicantPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+55 11 99999-9999"
                        {...field}
                        data-testid="input-edit-applicant-phone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedinUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://linkedin.com/in/..."
                        {...field}
                        data-testid="input-edit-applicant-linkedin"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={updateApplicant.isPending}
                data-testid="button-submit-edit-applicant"
              >
                {updateApplicant.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
